import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { rudderanalytics } from '../../../rudderanalytics';
import {
  Form,
  Input,
  Title,
  DownloadButton,
  Container,
  ErrorMessage,
} from './style';
import useDownloadWhitepaper, { WhitepaperName } from './useDownloadWhitepaper';

const schema = yup.object().shape({
  email: yup.string().email().required(),
  firstname: yup.string().min(2).required(),
  company: yup.string().min(2).required(),
});

const defaultValues = {
  email: '',
  firstname: '',
  company: '',
};
const defaultErrors = {
  email: '',
  firstname: '',
  company: '',
};
const defaultWasTouched = {
  email: false,
  firstname: false,
  company: false,
};

export interface DownloadFormProps {
  whitepaper: WhitepaperName;
}

// TODO Store the user's information in the localstorage,
// so that the user does not have to load the form twice.
// In case you have all the necessary information stored,
// just show the download button
const DownloadForm = ({ whitepaper }: DownloadFormProps) => {
  const { t } = useTranslation();

  const [downloadWhitepaper, downloadWhitepaperState] = useDownloadWhitepaper();
  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [wasTouched, setWasTouched] = useState(defaultWasTouched);

  const hasErrors = useMemo(() => {
    return Object.values(errors).some((error) => !!error);
  }, [errors]);

  const everyInputWasTouched = useMemo(() => {
    return Object.values(wasTouched).every((wasTouched) => wasTouched);
  }, [wasTouched]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      if (!isFormField(name)) return;
      const newValues = { ...values, [name]: value };
      const newErrors = { ...defaultErrors };
      try {
        schema.validateSync(newValues, { abortEarly: false });
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          error.inner.forEach((error) => {
            const name = error.path;
            if (!isFormField(name)) return;
            newErrors[name] = error.message;
          });
        }
      }
      setValues(newValues);
      setErrors(newErrors);
      if (wasTouched[name]) return;
      setWasTouched({ ...wasTouched, [name]: true });
    },
    [values, wasTouched]
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!everyInputWasTouched || hasErrors) return;
      rudderanalytics.identify(values.email, {
        email: values.email,
      });
      downloadWhitepaper({ whitepaper, ...values });
    },
    [everyInputWasTouched, hasErrors, whitepaper, values, downloadWhitepaper]
  );

  return (
    <Container className="animate__animated animate__fadeIn animate__faster animate__delay-2s">
      <Title>{t('FormTitle')}</Title>
      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          name="email"
          placeholder="Your work mail"
          value={values.email}
          onChange={handleChange}
        />
        <ErrorMessage>{wasTouched.email && errors.email}</ErrorMessage>
        <Input
          type="text"
          name="firstname"
          placeholder="First Name"
          value={values.firstname}
          onChange={handleChange}
        />
        <ErrorMessage>{wasTouched.firstname && errors.firstname}</ErrorMessage>
        <Input
          type="text"
          name="company"
          placeholder="Company Name"
          value={values.company}
          onChange={handleChange}
        />
        <ErrorMessage>{wasTouched.company && errors.company}</ErrorMessage>
        <DownloadButton
          disabled={
            !everyInputWasTouched ||
            hasErrors ||
            downloadWhitepaperState.loading ||
            !!downloadWhitepaperState.error
          }
        >
          {downloadWhitepaperState.loading
            ? 'Loading...'
            : 'Download whitepaper'}
        </DownloadButton>
        <ErrorMessage>
          {!!downloadWhitepaperState.error &&
            'Sorry, an error occurred, please try again later'}
        </ErrorMessage>
      </Form>
    </Container>
  );
};

function isFormField(name?: string): name is 'email' | 'firstname' | 'company' {
  return !!name && ['email', 'firstname', 'company'].includes(name);
}

export default DownloadForm;
